import * as yup from 'yup'
import { parseBudget } from '../filmBudget/calculator'

export const AI_TOOL_LIMITS = {
  EMAIL_MAX: 254,
  PROJECT_TITLE_MAX: 100,
  NAME_MAX: 100,
  COUNTRY_MAX: 100,
  NOTES_MAX: 2000,
  RUNTIME_MAX: 50,
  AGE_MAX: 30,
  BUDGET_STRING_MAX: 20,
  BUDGET_AMOUNT_MAX: 999999999999,
  SHOOT_DAYS_MAX: 730,
  LOCATIONS_MAX: 500,
  ACTORS_MAX: 500,
  SCENES_MAX: 1000,
  CAST_COUNT_MAX: 500,
  VFX_SHOTS_MAX: 10000,
  NIGHT_SHOOTS_MAX: 365,
}

const emailField = yup
  .string()
  .trim()
  .max(AI_TOOL_LIMITS.EMAIL_MAX, 'Email must be at most 254 characters long.')
  .required('Please enter your email address.')
  .email('Please enter a valid email address.')

const unameField = yup
  .string()
  .trim()
  .max(
    AI_TOOL_LIMITS.NAME_MAX,
    'Full name must be at most 100 characters long.',
  )
  .required('Please enter your full name.')

const positiveIntField = (max, label) =>
  yup.string().test(`${label}-range`, function (value) {
    if (value === '' || value === undefined || value === null) {
      return true
    }
    const num = parseInt(value, 10)
    if (Number.isNaN(num)) {
      return this.createError({
        message: `${label} must be a valid number.`,
      })
    }
    if (num > max) {
      return this.createError({
        message: `${label} must be at most ${max}.`,
      })
    }
    return true
  })

const requiredPositiveIntField = (max, label, requiredMessage) =>
  yup.string().test(`${label}-required`, function (value) {
    const num = parseInt(value, 10) || 0
    if (!num) {
      return this.createError({
        message: requiredMessage,
      })
    }
    if (num > max) {
      return this.createError({
        message: `${label} must be at most ${max}.`,
      })
    }
    return true
  })

export const productionAnalyzerSchema = yup.object({
  projectTitle: yup
    .string()
    .max(
      AI_TOOL_LIMITS.PROJECT_TITLE_MAX,
      'Project title must be at most 100 characters long.',
    ),
  email: emailField,
  uname: unameField,
  projectType: yup.string().required('Please select a project type.'),
  country: yup
    .string()
    .max(
      AI_TOOL_LIMITS.COUNTRY_MAX,
      'Country / region must be at most 100 characters long.',
    )
    .test(
      'not-only-digits',
      'Country / region cannot be only numbers.',
      (value) => !value?.trim() || !/^\d+$/.test(value.trim()),
    ),
  locations: requiredPositiveIntField(
    AI_TOOL_LIMITS.LOCATIONS_MAX,
    'Number of locations',
    'Please enter number of locations.',
  ),
  shootDays: requiredPositiveIntField(
    AI_TOOL_LIMITS.SHOOT_DAYS_MAX,
    'Shoot days',
    'Please enter shoot days.',
  ),
  budget: requiredPositiveIntField(
    AI_TOOL_LIMITS.BUDGET_AMOUNT_MAX,
    'Total budget',
    'Please enter total budget.',
  ),
  actors: requiredPositiveIntField(
    AI_TOOL_LIMITS.ACTORS_MAX,
    'Number of actors',
    'Please enter number of actors.',
  ),
  scenes: positiveIntField(AI_TOOL_LIMITS.SCENES_MAX, 'Number of scenes'),
  musicBudget: positiveIntField(
    AI_TOOL_LIMITS.BUDGET_AMOUNT_MAX,
    'Music budget',
  ),
  postBudget: positiveIntField(
    AI_TOOL_LIMITS.BUDGET_AMOUNT_MAX,
    'Post-production budget',
  ),
})

export const opportunityEngineSchema = yup.object({
  email: emailField,
  uname: unameField,
  selCrafts: yup
    .array()
    .min(1, 'Please select at least one required profession.')
    .required('Please select at least one required profession.'),
  selCT: yup.array(),
  country: yup.string(),
  state: yup.string(),
  city: yup.string(),
  lang: yup.string(),
  recency: yup.string(),
  gender: yup.string(),
  age: yup
    .string()
    .max(
      AI_TOOL_LIMITS.AGE_MAX,
      'Age range must be at most 30 characters long.',
    ),
})

export const budgetBreakdownSchema = yup.object({
  projTitle: yup
    .string()
    .max(
      AI_TOOL_LIMITS.PROJECT_TITLE_MAX,
      'Project title must be at most 100 characters long.',
    ),
  email: emailField,
  uname: unameField,
  format: yup.string().required('Please select a production format.'),
  genre: yup.string(),
  market: yup.string(),
  totalBudget: yup
    .string()
    .trim()
    .max(
      AI_TOOL_LIMITS.BUDGET_STRING_MAX,
      'Total budget must be at most 20 characters long.',
    )
    .required('Please enter your total budget.')
    .test(
      'min-budget',
      'Please enter a valid total budget amount.',
      (value) => {
        if (!value?.trim()) return false
        return parseBudget(value) >= 1000
      },
    )
    .test(
      'max-budget',
      'Total budget exceeds the maximum allowed amount.',
      (value) => {
        if (!value?.trim()) return true
        return parseBudget(value) <= AI_TOOL_LIMITS.BUDGET_AMOUNT_MAX
      },
    ),
  shootDays: positiveIntField(AI_TOOL_LIMITS.SHOOT_DAYS_MAX, 'Shoot days'),
  runtime: yup
    .string()
    .max(
      AI_TOOL_LIMITS.RUNTIME_MAX,
      'Runtime must be at most 50 characters long.',
    ),
  castCount: positiveIntField(AI_TOOL_LIMITS.CAST_COUNT_MAX, 'Principal cast'),
  locations: positiveIntField(AI_TOOL_LIMITS.LOCATIONS_MAX, 'Shoot locations'),
  nightShoots: positiveIntField(
    AI_TOOL_LIMITS.NIGHT_SHOOTS_MAX,
    'Night shoots',
  ),
  vfxShots: positiveIntField(AI_TOOL_LIMITS.VFX_SHOTS_MAX, 'VFX shots'),
  notes: yup
    .string()
    .max(
      AI_TOOL_LIMITS.NOTES_MAX,
      'Additional notes must be at most 2000 characters long.',
    ),
})

export function getFormBannerError(formik, apiError = '') {
  if (apiError) return apiError
  if (formik.submitCount > 0 && formik.errors._form) {
    return formik.errors._form
  }
  return ''
}
